import type { MinimalWebSocket } from '@atlas/collaboration/websocket/platform/minimal-websocket';
import { fireAndForget } from '@core/util/fireAndForget';
import type { ResultError } from '@core/util/result';
import { err, ok, type Result } from 'neverthrow';
import { v7 as uuid7 } from 'uuid';

type PromiseHandler = Promise<any> & {
  resolve: (value?: any) => void;
  resolved: boolean;
};

export function createWebsocketPromiseChain<
  FunctionInputArgType extends any[],
  const L extends Array<{
    filter: (data: any) => boolean;
    handler: (
      data: WebsocketJobSubmissionSuccessResponse<any>,
      ...args: FunctionInputArgType
    ) => any;
  }>,
  Results = {
    [K in keyof L]: Promise<ReturnType<L[K]['handler']> | undefined>;
  },
>(
  {
    jobType,
    getInputData,
    timeoutMs,
    ws,
  }: {
    jobType: JobTypeEnum;
    getInputData: (...args: FunctionInputArgType) => any | Promise<any>;
    timeoutMs: number;
    ws: MinimalWebSocket;
  },
  listenersChain: L
) {
  const requestId = uuid7();

  const websocketErrorFilter = (
    response: any
  ): Result<void, ResultError<'INVALID_RESPONSE' | 'SERVICE_ERROR'>[]> => {
    if (isJobSubmissionErrorResponse(response)) {
      return err([{ code: 'SERVICE_ERROR', message: response.error }]);
    }

    if (!isJobSubmissionSuccessResponse(response)) {
      return err([
        { code: 'INVALID_RESPONSE', message: 'Invalid response format' },
      ]);
    }

    if (
      isDocumentProcessResponse(response.data) &&
      isDocumentProcessResponseError(response.data)
    ) {
      return err([{ code: 'SERVICE_ERROR', message: response.data.message }]);
    }

    return ok(undefined);
  };

  const createResultPromise = (): PromiseHandler => {
    let resolvePromise: (value: any) => void;
    const promise = new Promise((resolve) => {
      resolvePromise = resolve;
      setTimeout(() => {
        if (!promise.resolved) {
          console.error(`${jobType} failed: TIMEOUT`);
          resolve(undefined);
        }
      }, timeoutMs);
    }) as PromiseHandler;
    fireAndForget(
      promise.finally(() => {
        promise.resolved = true;
      }),
      'docx-upload'
    );

    return Object.assign(promise, {
      resolve: resolvePromise!,
      resolved: false,
    });
  };

  const invokeWebsocket = (...args: FunctionInputArgType): Results => {
    const promiseHandlerListeners = listenersChain.map(
      (listener) => [createResultPromise(), listener] as const
    );
    const results = promiseHandlerListeners.map((x) => x[0]);
    const messageHandler = (ev: MessageEvent) => {
      try {
        const response = JSON.parse(ev.data);
        if (response?.macroRequestId !== requestId) return;

        const errorCheck = websocketErrorFilter(response);
        if (errorCheck.isErr()) {
          for (const [promise] of promiseHandlerListeners) {
            console.error(errorCheck.error);
            if (!promise.resolved) promise.resolve(undefined);
          }
          return;
        }

        const listener = promiseHandlerListeners.find(([, { filter }]) =>
          filter(response)
        );
        if (!listener) return;
        const [promise, { handler }] = listener;
        try {
          if (promise.resolved) {
            console.error('DSS WS handler already resolved');
          } else {
            promise.resolve(handler(response, ...args));
          }
        } catch (e) {
          console.error(e);
          if (!promise.resolved) {
            promise.resolve(undefined);
          }
        }
      } catch (e) {
        console.error(e);
        for (const [promise] of promiseHandlerListeners) {
          if (!promise.resolved) promise.resolve(undefined);
        }
      }
    };

    const closeHandler = () => {
      console.error(`${jobType} failed: WebSocket closed`);
      for (const [promise] of promiseHandlerListeners) {
        if (!promise.resolved) promise.resolve(undefined);
      }
    };

    ws.addEventListener('message', messageHandler);
    ws.addEventListener('close', closeHandler);
    fireAndForget(
      Promise.allSettled(results).then(() => {
        ws.removeEventListener('message', messageHandler);
        ws.removeEventListener('close', closeHandler);
      }),
      'docx-upload'
    );

    let maybeInputData = getInputData(...args);
    if (!(maybeInputData instanceof Promise)) {
      maybeInputData = Promise.resolve(maybeInputData);
    }

    maybeInputData.then((inputData: any) => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.error(
          `${jobType} failed: WebSocket is not open (readyState: ${ws.readyState})`
        );
        for (const [promise] of promiseHandlerListeners) {
          if (!promise.resolved) promise.resolve(undefined);
        }
        return;
      }

      ws.send(
        JSON.stringify({
          requestId,
          action: jobType,
          data: inputData,
        })
      );
    });

    return results as Results;
  };

  return invokeWebsocket;
}

// Supporting types and interfaces
export enum JobTypeEnum {
  DocxUpload = 'docx_upload',
}

const SUCCESS_STATUS = 'Success';
const ERROR_STATUS = 'Error';

type DocumentProcessSuccessResponse = {
  error: false;
  data: Record<string, any>;
};

type DocumentProcessErrorResponse = {
  error: true;
  message: string;
};

type DocumentProcessResponse =
  | DocumentProcessSuccessResponse
  | DocumentProcessErrorResponse;

type WebsocketJobSubmissionErrorResponse = {
  jobId?: string;
  macroRequestId?: string;
  event?: string;
  requestBody?: any;
  error: string;
  status: typeof ERROR_STATUS;
};

type WebsocketJobSubmissionSuccessResponseBase = {
  jobId: string;
  macroRequestId: string;
  event: string;
  status: typeof SUCCESS_STATUS;
  data: any;
};

export type WebsocketJobSubmissionSuccessResponse<T = unknown> = Omit<
  WebsocketJobSubmissionSuccessResponseBase,
  'data'
> & {
  data: T;
};

// Type Guards
function isDocumentProcessResponse(data: any): data is DocumentProcessResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof data.error === 'boolean' &&
    ((!data.error && 'data' in data && typeof data.data === 'object') ||
      (data.error && 'message' in data && typeof data.message === 'string'))
  );
}

function isDocumentProcessResponseError(
  response: DocumentProcessResponse
): response is DocumentProcessErrorResponse {
  return response.error;
}

function isJobSubmissionErrorResponse(
  response?: any
): response is WebsocketJobSubmissionErrorResponse {
  return response?.status === ERROR_STATUS;
}

function isJobSubmissionSuccessResponse<T = unknown>(
  response?: any
): response is WebsocketJobSubmissionSuccessResponse<T> {
  return response?.status === SUCCESS_STATUS;
}
